<script setup>
/**
 * The options, in the order WhatsApp shows them — `TaskStepList`'s row grammar: the number is
 * the label, the row's controls sit on its label line as icon buttons (keyboard- and
 * screen-reader-usable, unlike a drag handle), and rows key on a local `key` so a reorder never
 * hands one box its neighbour's text.
 *
 * ONE BOX PER OPTION, two languages (P-7.1): `BilingualBox`, typed once, the other language
 * suggested for a price. Its counter is against the limit that applies RIGHT NOW: 20 while the
 * menu has three options or fewer (they are buttons), 24 once it is a list. The limit moves
 * when an option is added or removed, which is why it is shown, not only enforced.
 *
 * "When tapped" is a real `<select>` whose first choice is "Just answer the question" with
 * value `''` — `WasloSelect`'s placeholder option is disabled by design, so "none" has to be a
 * choice of its own. When the tasks could not be read the picker is replaced by a sentence and
 * every option keeps the task it already had (the draft is untouched).
 */
import { computed } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

import BilingualBox from './task/BilingualBox.vue';
import WasloSelect from './WasloSelect.vue';
import { useWasloI18n } from '../i18n.js';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';
import { toIntlLocale } from '../helpers/intl.js';
import {
  BUTTONS_UP_TO,
  MAX_OPTIONS,
  moveRow,
  optionRow,
  optionTitleLimit,
} from '../helpers/menu-draft.js';

const props = defineProps({
  /** `[{ key, id, titleEn, titleAr, taskId }]` — see `helpers/menu-draft.js`. */
  modelValue: { type: Array, default: () => [] },
  /** `[{ id, title, enabled }]`, or null when that read failed. */
  tasks: { type: Array, default: null },
  /** One message for the whole section, from `validateDraft`. */
  message: { type: String, default: '' },
  /** The account's "Your customers write in" (P-7.1). */
  languages: { type: String, default: 'both' },
});

const emit = defineEmits(['update:modelValue']);

const { t, locale } = useWasloI18n();

/**
 * THE OTHER HALF OF THE CROSS-LINK.
 *
 * `TaskCard` reaches the Menus tab; this reaches the task. A row that names a task is the
 * one place in the product where a menu and a task are joined, and until now the only thing
 * it offered was the join itself — the owner could see that "Book a fitting" was attached
 * and had no way to go and look at it, four clicks from here through a sidebar row that no
 * longer exists.
 *
 * The account comes from the ROUTE rather than from a prop threaded through `MenuForm`:
 * `useWasloAccountId` reads `route.params.accountId`, which is the same authority every
 * other screen in this section uses, and threading it two components deep would be a second
 * spelling of a value that cannot disagree with itself.
 *
 * Like `TaskCard`'s own edit link, it lands on the SPINE (`?step=steps`) — the question
 * from here is always "what does that task actually do", never "what is it called".
 */
const accountId = useWasloAccountId();

const taskLink = taskId =>
  accountId.value === null || !Number.isInteger(taskId)
    ? null
    : {
        path: frontendURL(`accounts/${accountId.value}/waslo/tasks/${taskId}`),
        query: { step: 'steps' },
      };

const format = computed(
  () => new Intl.NumberFormat(toIntlLocale(locale.value))
);
const isFull = computed(() => props.modelValue.length >= MAX_OPTIONS);
const limit = computed(() => optionTitleLimit(props.modelValue.length));
const limitNote = computed(() =>
  props.modelValue.length <= BUTTONS_UP_TO
    ? t('WASLO.MENUS.EDITOR.OPTIONS.BUTTONS_NOTE')
    : t('WASLO.MENUS.EDITOR.OPTIONS.LIST_NOTE')
);

const taskChoices = computed(() => [
  { value: '', label: t('WASLO.MENUS.EDITOR.OPTIONS.NO_TASK') },
  ...(props.tasks ?? []).map(task => ({
    value: String(task.id),
    label: task.enabled
      ? task.title
      : t('WASLO.MENUS.EDITOR.OPTIONS.TASK_OFF', { title: task.title }),
  })),
]);

const label = index =>
  t('WASLO.MENUS.EDITOR.OPTIONS.LABEL', {
    number: format.value.format(index + 1),
  });

const setField = (index, changes) =>
  emit(
    'update:modelValue',
    props.modelValue.map((row, at) =>
      at === index ? { ...row, ...changes } : row
    )
  );

const setTask = (index, value) =>
  setField(index, { taskId: value === '' ? null : Number(value) });

const move = (index, delta) =>
  emit('update:modelValue', moveRow(props.modelValue, index, delta));

const removeAt = index =>
  emit(
    'update:modelValue',
    props.modelValue.filter((row, at) => at !== index)
  );

const add = () => emit('update:modelValue', [...props.modelValue, optionRow()]);
</script>

<template>
  <div class="flex flex-col gap-4" data-waslo-menu-options>
    <div
      v-for="(row, index) in modelValue"
      :key="row.key"
      class="flex flex-col gap-3 p-4 outline outline-1 rounded-xl outline-n-weak bg-n-solid-1"
      data-waslo-menu-option
    >
      <div class="flex items-center justify-between gap-3">
        <h4 class="mb-0 text-sm font-medium text-n-slate-12">
          {{ label(index) }}
        </h4>
        <div class="flex items-center gap-1 shrink-0">
          <button
            type="button"
            class="grid rounded-md place-items-center size-7 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-40 disabled:hover:bg-transparent"
            :disabled="index === 0"
            :aria-label="t('WASLO.MENUS.EDITOR.OPTIONS.UP')"
            :title="t('WASLO.MENUS.EDITOR.OPTIONS.UP')"
            data-waslo-menu-option-up
            @click="move(index, -1)"
          >
            <span class="size-4 i-lucide-arrow-up" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="grid rounded-md place-items-center size-7 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-40 disabled:hover:bg-transparent"
            :disabled="index === modelValue.length - 1"
            :aria-label="t('WASLO.MENUS.EDITOR.OPTIONS.DOWN')"
            :title="t('WASLO.MENUS.EDITOR.OPTIONS.DOWN')"
            data-waslo-menu-option-down
            @click="move(index, 1)"
          >
            <span class="size-4 i-lucide-arrow-down" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="grid rounded-md place-items-center size-7 text-n-slate-11 hover:text-n-ruby-9 hover:bg-n-alpha-2"
            :aria-label="t('WASLO.MENUS.EDITOR.OPTIONS.REMOVE')"
            :title="t('WASLO.MENUS.EDITOR.OPTIONS.REMOVE')"
            data-waslo-menu-option-remove
            @click="removeAt(index)"
          >
            <span class="size-4 i-lucide-trash-2" aria-hidden="true" />
          </button>
        </div>
      </div>

      <BilingualBox
        :en="row.titleEn"
        :ar="row.titleAr"
        :label="t('WASLO.MENUS.EDITOR.OPTIONS.TITLE')"
        :max-length="limit"
        counter
        kind="menu_row_title"
        :languages="languages"
        data-waslo-menu-option-title
        @update:en="value => setField(index, { titleEn: value })"
        @update:ar="value => setField(index, { titleAr: value })"
        @update:pair="
          pair => setField(index, { titleEn: pair.en, titleAr: pair.ar })
        "
      />

      <div v-if="tasks" class="flex flex-col gap-1.5">
        <WasloSelect
          :model-value="row.taskId === null ? '' : String(row.taskId)"
          :label="t('WASLO.MENUS.EDITOR.OPTIONS.TASK')"
          :options="taskChoices"
          data-waslo-menu-option-task
          @update:model-value="value => setTask(index, value)"
        />
        <!--
          Only when there IS one. A link that goes nowhere on nine rows out of ten is worse
          chrome than the hint below, and "Just answer the question" has no task to open.
        -->
        <router-link
          v-if="taskLink(row.taskId)"
          :to="taskLink(row.taskId)"
          class="text-xs font-medium text-n-blue-11 hover:underline w-fit"
          data-waslo-menu-option-open-task
        >
          {{ t('WASLO.MENUS.EDITOR.OPTIONS.OPEN_TASK') }}
        </router-link>
      </div>
    </div>

    <p
      v-if="tasks === null"
      class="mb-0 text-sm text-n-slate-11"
      data-waslo-menu-tasks-unavailable
    >
      {{ t('WASLO.MENUS.EDITOR.OPTIONS.TASKS_UNAVAILABLE') }}
    </p>
    <!-- One hint for the whole section, not one per row (up to 10 identical sentences is
         exactly the per-row chrome this folder avoids elsewhere — see TaskStepList's near-
         limit counter). Shown only when the pickers themselves are — `tasks` loaded. -->
    <p
      v-if="tasks"
      class="mb-0 text-sm text-n-slate-11"
      data-waslo-menu-task-hint
    >
      {{ t('WASLO.MENUS.EDITOR.OPTIONS.TASK_HINT') }}
    </p>
    <p class="mb-0 text-sm text-n-slate-11" data-waslo-menu-options-limit>
      {{ limitNote }}
    </p>
    <p
      v-if="message"
      class="mb-0 text-sm text-n-ruby-9"
      data-waslo-menu-options-error
    >
      {{ message }}
    </p>

    <div class="flex items-center gap-3">
      <Button
        :label="t('WASLO.MENUS.EDITOR.OPTIONS.ADD')"
        icon="i-lucide-plus"
        variant="faded"
        color="slate"
        size="sm"
        :disabled="isFull"
        data-waslo-menu-option-add
        @click="add"
      />
      <span v-if="isFull" class="text-sm text-n-slate-11">
        {{ t('WASLO.MENUS.EDITOR.OPTIONS.FULL') }}
      </span>
    </div>
  </div>
</template>
